import React, { useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import {
    Box,
    Typography,
    CircularProgress,
    Grid,
    List,
    ListItem,
    FormLabel,
    DateRangePicker,
    InputAdornment,
    Button,
    Card,
} from '@deere/fuel-react';
import styles from './WorkforceMyJobBidsHistory.module.css';
import { useNavigate } from 'react-router-dom';
import { AlertBox } from '../../components/AlertBox';
import { DateTime } from 'luxon';
import { CalendarMonth } from '@mui/icons-material';
import { WorkforceContext } from '../../context/WorkforceContext';
import { useContext } from 'react';
import { WORKFORCE_JOB_BID_STATUS } from '../../constants.js';
import { LoggedInContext } from '../../context/LoggedInContext';
import { errorBannerFromResponse } from '../../utils/errorBanner';

const WORKFORCE_BID_ASSIGNMENT_DISPLAY_TEXT = {
    WITHDRAWN: 'Bid Withdrawn',
    SUCCESSFUL: 'Successful',
    UNSUCCESSFUL: 'Unsuccessful',
    NOT_FINALIZED: 'Successful Bidders Not Finalized',
    CANCELED: 'Job posted in error',
};

const ClosedBidRow = ({ myBidHist }) => {
    const navigate = useNavigate();

    const navigateTo = () => {
        const navigateURL = '/my-bid-history/details/';
        if (
            myBidHist.bidStatus !== WORKFORCE_JOB_BID_STATUS.NOT_FINALIZED &&
            myBidHist.bidStatus !== WORKFORCE_JOB_BID_STATUS.CANCELED
        ) {
            navigate(navigateURL + myBidHist.id);
        }
    };

    return (
        <ListItem className={styles.jobBidRow}>
            <Grid
                container
                item
                xs={12}
                onClick={navigateTo}
                className={styles.jobBidRowInner}
            >
                <Grid
                    item
                    xs={12}
                    sm={6}
                    className={
                        myBidHist.bidStatus !==
                            WORKFORCE_JOB_BID_STATUS.NOT_FINALIZED &&
                        myBidHist.bidStatus !==
                            WORKFORCE_JOB_BID_STATUS.CANCELED
                            ? styles.jobBidRowTitle
                            : styles.boldData
                    }
                >
                    <span>
                        {' '}
                        {myBidHist.classification} #{myBidHist.id}{' '}
                    </span>
                    <span>- ({myBidHist.noOfOpenings}) openings</span>
                </Grid>
                <Grid item sm={6} className={styles.jobBiddersSection}>
                    {myBidHist.bidStatus === 'SUCCESSFUL' && (
                        <span className={styles.boldData}>
                            {
                                WORKFORCE_BID_ASSIGNMENT_DISPLAY_TEXT[
                                    myBidHist.bidStatus
                                ]
                            }
                        </span>
                    )}
                    {myBidHist.bidStatus !== 'SUCCESSFUL' && (
                        <span>
                            {
                                WORKFORCE_BID_ASSIGNMENT_DISPLAY_TEXT[
                                    myBidHist.bidStatus
                                ]
                            }
                        </span>
                    )}
                </Grid>
                <Grid item xs={12} className={styles.jobBidInfoSection}>
                    <span>Facility: {myBidHist.facility}</span>
                    <span>Dept: {myBidHist.department}</span>
                    <span>Job Grade: {myBidHist.jobGrade}</span>
                    <span>
                        Bid Close:{' '}
                        {myBidHist.endDate != null &&
                            DateTime.fromISO(myBidHist.endDate).toFormat(
                                'LL/dd/yy'
                            )}
                    </span>
                    <span>
                        Bid Withdrawal Close:{' '}
                        {myBidHist.bidWithDrawalEndDt != null &&
                            DateTime.fromISO(
                                myBidHist.bidWithDrawalEndDt
                            ).toFormat('LL/dd/yy')}
                    </span>
                </Grid>
                {myBidHist.bidStatus === 'WITHDRAWN' && (
                    <>
                        <Grid item xs={12} className={styles.jobBidInfoSection}>
                            <span className={styles.boldData}>
                                Bid Withdrawn:{' '}
                            </span>
                            {myBidHist.updatedTs != null &&
                                DateTime.fromISO(myBidHist.updatedTs).toFormat(
                                    'MM/dd/yyyy hh:mm a'
                                )}
                        </Grid>
                        <Grid item xs={12} className={styles.jobBidInfoSection}>
                            <span className={styles.boldData}>
                                Withdrawn By:{' '}
                            </span>
                            {myBidHist.updatedBy}
                        </Grid>
                    </>
                )}
            </Grid>
        </ListItem>
    );
};

export const WorkforceMyJobBidsHistory = () => {
    const [myBidsHistory, setMyBidsHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { authState } = useOktaAuth();
    const [alertMsg, setAlertMsg] = useState();
    const [alertVariant, setAlertVariant] = useState(false);
    const start = DateTime.now().startOf('month').toJSDate();
    const end = DateTime.now().toJSDate();
    const [dateRange, setDateRange] = useState([start, end]);
    const { biddingGroup, loading } = useContext(WorkforceContext);
    const { makeRequest, impersonatedId } = useContext(LoggedInContext);

    const setContractYearDate = () => {
        if (biddingGroup[0].contractStartDate === null) {
            setAlertVariant('error');
            setAlertMsg(
                'We are unable to retrieve information for this employee. Please try again later.'
            );
            return;
        }
        setDateRange([
            DateTime.fromISO(biddingGroup[0].contractStartDate).toFormat(
                'dd-LLL-yyyy'
            ),
            end,
        ]);
        dateRangeHandler([
            DateTime.fromISO(biddingGroup[0].contractStartDate).toJSDate(),
            end,
        ]);
    };

    const dateRangeHandler = async (dates) => {
        if (!dates[1]) {
            return;
        } else {
            setDateRange(dates);
            const startDate = DateTime.fromJSDate(dates[0]).toISODate();
            const endDate = DateTime.fromJSDate(dates[1]).toISODate();
            setIsLoading(true);

            try {
                const response = await makeRequest(
                    process.env.REACT_APP_API_ENDPOINT +
                        '/bidHistoryClosed/' +
                        (impersonatedId != null && impersonatedId.trim() !== ''
                            ? impersonatedId
                            : authState.accessToken.claims.userID) +
                        '?' +
                        'startDate=' +
                        startDate +
                        '&endDate=' +
                        endDate
                );
                if (response.status === 200) {
                    const responseData = await response.json();
                    const jobBids = responseData.map((bid) => ({
                        bidWithDrawalEndDt: bid.withDrawalEndDate,
                        classification: bid.jobClassification,
                        department: bid.jobDept,
                        endDate: bid.endDate,
                        facility: bid.facility,
                        id: bid.jobNumber,
                        jobGrade: bid.jobGrade,
                        noOfOpenings: bid.openings,
                        preference: bid.empPreference,
                        bidStatus: bid.bidStatus,
                        racfId: bid.racfId,
                        updatedBy: bid.updatedBy,
                        updatedTs: bid.updatedTs,
                    }));
                    setIsLoading(false);
                    if (jobBids.length > 0) {
                        setMyBidsHistory(jobBids);
                    }
                    setIsLoading(false);
                    setAlertVariant(null);
                } else {
                    setAlertVariant('error');
                    setIsLoading(false);
                    if (response.status === 404) {
                        setAlertMsg(
                            'There are no job bids for this employee for this date range.'
                        );
                    } else {
                        setAlertMsg(
                            errorBannerFromResponse(
                                response,
                                'We are unable to retrieve information for this employee. Please try again later.'
                            ).message
                        );
                    }
                }
            } catch (err) {
                setAlertMsg(
                    errorBannerFromResponse(
                        err,
                        'We are unable to retrieve information for this employee. Please try again later.'
                    ).message
                );
                setAlertVariant('error');
            }
        }
    };

    useEffect(() => {
        let ignore = false;
        const fetchBids = async () => {
            setIsLoading(true);
            const firstDateOfMonth = DateTime.now()
                .startOf('month')
                .toISODate();
            const today = DateTime.now().toISODate();
            try {
                if (!ignore) {
                    const response = await makeRequest(
                        process.env.REACT_APP_API_ENDPOINT +
                            '/bidHistoryClosed/' +
                            (impersonatedId != null &&
                            impersonatedId.trim() !== ''
                                ? impersonatedId
                                : authState.accessToken.claims.userID) +
                            '?' +
                            'startDate=' +
                            firstDateOfMonth +
                            '&endDate=' +
                            today
                    );
                    if (response.status === 200) {
                        const responseData = await response.json();
                        const jobBids = responseData.map((bid) => ({
                            bidWithDrawalEndDt: bid.withDrawalEndDate,
                            classification: bid.jobClassification,
                            department: bid.jobDept,
                            endDate: bid.endDate,
                            facility: bid.facility,
                            id: bid.jobNumber,
                            jobGrade: bid.jobGrade,
                            noOfOpenings: bid.openings,
                            preference: bid.empPreference,
                            bidStatus: bid.bidStatus,
                            racfId: bid.racfId,
                            updatedBy: bid.updatedBy,
                            updatedTs: bid.updatedTs,
                        }));
                        setIsLoading(false);
                        if (jobBids.length > 0) {
                            setMyBidsHistory(jobBids);
                        }
                    } else {
                        setAlertVariant('error');
                        setIsLoading(false);
                        if (response.status === 404) {
                            setAlertMsg(
                                'There are no job bids for this employee for this date range.'
                            );
                        } else {
                            setAlertMsg(
                                errorBannerFromResponse(
                                    response,
                                    'We are unable to retrieve information for this employee. Please try again later.'
                                ).message
                            );
                        }
                    }
                }
            } catch (err) {
                setAlertMsg(
                    errorBannerFromResponse(
                        err,
                        'Your job bids cannot be loaded at this time. Please try again later.'
                    ).message
                );
                setAlertVariant('error');
            }
        };

        fetchBids();
        // cleanup function that will ignore future results of fetch triggered by the useEffect hook
        return () => {
            ignore = true;
        };
    }, [authState, impersonatedId, makeRequest]);

    return (
        <div className={styles.parentContainer}>
            <Box className={styles.contentToContainer}>
                <Box>
                    {alertVariant && (
                        <AlertBox msg={alertMsg} variant={alertVariant} />
                    )}
                </Box>
                <br></br>
                <Box
                    className={styles.headerContainer}
                    display="block"
                    bgcolor="background.default"
                >
                    <Typography variant="h1" className={styles.heading}>
                        My Bid History
                    </Typography>
                </Box>
                <Box className={styles.jobDirectionsBox}>
                    <Typography>
                        Choose a Date to view all jobs you bid on. Click on a
                        job bid number to view job details and a list of
                        successful bidders.
                    </Typography>
                    <br />
                    <Typography>* Required fields.</Typography>
                </Box>
                {!isLoading && !loading && (
                    <Grid container justifyContent="space-between">
                        <Grid
                            item
                            sm={6}
                            xs={12}
                            className={styles.dateRangeStyle}
                        >
                            <FormLabel required={true}>
                                * Month and Year
                            </FormLabel>

                            <DateRangePicker
                                size="small"
                                value={dateRange}
                                onChange={(dates) => {
                                    dateRangeHandler(dates);
                                }}
                                startDatePlaceholder="Select Start Date"
                                endDatePlaceholder="Select End Date"
                                StartInputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <CalendarMonth />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid
                            item
                            sm={6}
                            xs={12}
                            className={styles.buttonStyle}
                        >
                            <Button
                                variant="primary"
                                className={styles.pageLevelButton}
                                onClick={setContractYearDate}
                            >
                                View My Bids
                            </Button>
                        </Grid>
                    </Grid>
                )}
                {(isLoading || loading) && (
                    <CircularProgress
                        size="lg"
                        variant="indeterminate"
                        title={'Loading'}
                    />
                )}

                {!isLoading && myBidsHistory.length > 0 && (
                    <Box mt={10}>
                        <Card>
                            <List>
                                {myBidsHistory.map((myBidHist, index) => (
                                    <ClosedBidRow
                                        myBidHist={myBidHist}
                                        key={index}
                                    />
                                ))}
                            </List>
                        </Card>
                    </Box>
                )}
            </Box>
        </div>
    );
};
